/**
 * Helpers para mapear nodos BOQ takeoff → totales de plantilla semanal.
 */

export const ROW_KIND_DISCIPLINE = 1
export const ROW_KIND_STAGE = 2

export function mapBoqRootToWeeklyNode(raw) {
  return {
    id: raw.id,
    code: raw.code || '',
    name: raw.name || '',
    rowKind: raw.row_kind != null ? Number(raw.row_kind) : null,
    childrenCount: Number(raw.children_count) || 0,
    availableHoursPerWeek: toNullableNumber(raw.available_hours_per_week),
    takeoffStats: {
      unit: raw.unit ?? null,
      total_scope: raw.total_scope ?? null,
      executed_accum: raw.executed_accum ?? null,
      balance: raw.balance ?? null,
      scheduled_man_hours: raw.scheduled_man_hours ?? null,
      hh_to_finish: raw.hh_to_finish ?? null,
      people_needed: raw.people_needed ?? null,
      adjusted_man_hour_index: raw.adjusted_man_hour_index ?? null,
    },
    monthlyDistributions: Array.isArray(raw.monthly_distributions)
      ? raw.monthly_distributions
      : [],
    weeklyDraft: weeklyDraftFromRaw(raw),
    savedWeekly: weeklyDraftFromRaw(raw),
    savingWeekly: {},
    savedAdjustedIndex: raw.adjusted_man_hour_index ?? null,
    savingAdjustedIndex: false,
    plannedQuantityPerWeek: weekQuantityMapFromRaw(raw, 'planned_quantity_per_week'),
    actualQuantityPerWeek: weekQuantityMapFromRaw(raw, 'actual_quantity_per_week'),
    plannedManHoursPerWeek: weekQuantityMapFromRaw(raw, 'planned_man_hours_per_week'),
    availableManHoursPerWeek: weekQuantityMapFromRaw(raw, 'available_man_hours_per_week'),
    scheduledManHoursPerWeek: weekQuantityMapFromRaw(raw, 'scheduled_man_hours_per_week'),
    actualManHoursPerWeek: weekQuantityMapFromRaw(raw, 'actual_man_hours_per_week'),
    assignedPeoplePerWeek: weekQuantityMapFromRaw(raw, 'assigned_people_per_week'),
  }
}

/** Mapa week_number → FTD planif. desde planned_quantity_per_week del API. */
export function plannedQuantityPerWeekFromRaw(raw) {
  return weekQuantityMapFromRaw(raw, 'planned_quantity_per_week')
}

/** Normaliza mapas { week: number } desde atributos JSON del API. */
export function weekQuantityMapFromRaw(raw, attribute) {
  let source = raw && raw[attribute]
  if (typeof source === 'string' && source !== '') {
    try {
      source = JSON.parse(source)
    } catch (e) {
      source = null
    }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return {}
  }
  const map = {}
  Object.keys(source).forEach((key) => {
    const week = Number(key)
    if (!week) {
      return
    }
    const value = toNullableNumber(source[key])
    if (value != null) {
      map[String(week)] = value
    }
  })
  return map
}

/** Lee un valor de mapa semanal (clave numérica o string). */
export function quantityForWeek(map, week) {
  const source = map || {}
  const key = String(week)
  const value = source[key] != null ? source[key] : source[week]
  return toNullableNumber(value)
}

/** Lee FTD planif. de una semana (acepta clave numérica o string). */
export function plannedQuantityForWeek(node, week) {
  return quantityForWeek(node && node.plannedQuantityPerWeek, week)
}

/** Lee FTD real de una semana (acumulado ejecutado hasta ahora). */
export function actualQuantityForWeek(node, week) {
  return quantityForWeek(node && node.actualQuantityPerWeek, week)
}

/** Lee HH planif. de una semana (índice ajustado × FTD planif.). */
export function plannedManHoursForWeek(node, week) {
  return quantityForWeek(node && node.plannedManHoursPerWeek, week)
}

/** Lee HH disponibles de una semana (efectivo × QNT premisas). */
export function availableManHoursForWeek(node, week) {
  return quantityForWeek(node && node.availableManHoursPerWeek, week)
}

/** Lee HH programadas de una semana (índice ajustado × FTD program.). */
export function scheduledManHoursForWeek(node, week) {
  return quantityForWeek(node && node.scheduledManHoursPerWeek, week)
}

/** Lee HH reales de una semana (índice ajustado × FTD real). */
export function actualManHoursForWeek(node, week) {
  return quantityForWeek(node && node.actualManHoursPerWeek, week)
}

/**
 * HH de una etapa/semana solo desde mapas del API (sin fallbacks locales).
 * @returns {{hhPlan:number|null, hhDisp:number|null, hhProg:number|null, hhReal:number|null}}
 */
export function weekHhFromApi(node, week) {
  return {
    hhPlan: plannedManHoursForWeek(node, week),
    hhDisp: availableManHoursForWeek(node, week),
    hhProg: scheduledManHoursForWeek(node, week),
    hhReal: actualManHoursForWeek(node, week),
  }
}

/** Efectivo (personas): stage desde draft; disciplina desde roll-up API. */
export function assignedPeopleForWeek(node, week) {
  if (isStageNode(node)) {
    const draft = (node.weeklyDraft && node.weeklyDraft[week]) || {}
    return toNullableNumber(draft.assigned_people)
  }
  return quantityForWeek(node && node.assignedPeoplePerWeek, week)
}

/**
 * Totales de fila grupo (disciplina) desde mapas API del propio nodo (roll-up PHP).
 * @returns {{ef:number|null, hhPlan:number|null, hhDisp:number|null, hhProg:number|null, hhReal:number|null}}
 */
export function groupWeekFromApi(node, week) {
  const hh = weekHhFromApi(node, week)
  return {
    ef: assignedPeopleForWeek(node, week),
    hhPlan: hh.hhPlan,
    hhDisp: hh.hhDisp,
    hhProg: hh.hhProg,
    hhReal: hh.hhReal,
  }
}

/**
 * Reemplaza mapas HH disp/prog con los derivados del PUT (PHP).
 * Fuente única: mismas fórmulas que takeoffWeeklyQuantityMapsSql.
 */
export function applyWeeklyManHoursFromApi(node, payload) {
  if (!node || !payload) {
    return
  }
  if (payload.available_hours_per_week != null) {
    node.availableHoursPerWeek = toNullableNumber(payload.available_hours_per_week)
  }

  node.availableManHoursPerWeek = weekQuantityMapFromRaw(payload, 'available_man_hours_per_week')
  node.scheduledManHoursPerWeek = weekQuantityMapFromRaw(payload, 'scheduled_man_hours_per_week')
}

/**
 * Totales de pie: suma filas raíz (disciplinas con roll-up o stages).
 * Evita doble conteo (no suma stages hijos si ya están en el roll-up del padre).
 * @returns {{ef:number|null, hhPlan:number|null, hhDisp:number|null, hhProg:number|null, hhReal:number|null, warn:boolean}}
 */
export function sumRootWeekTotalsFromApi(roots, week) {
  let ef = 0
  let hhPlan = 0
  let hhDisp = 0
  let hhProg = 0
  let hhReal = 0
  let hasEf = false
  let hasPlan = false
  let hasDisp = false
  let hasProg = false
  let hasReal = false

  for (const node of roots || []) {
    const row = isStageNode(node)
      ? {
        ef: assignedPeopleForWeek(node, week),
        ...weekHhFromApi(node, week),
      }
      : groupWeekFromApi(node, week)

    if (row.ef != null) {
      hasEf = true
      ef += row.ef
    }
    if (row.hhPlan != null) {
      hasPlan = true
      hhPlan += row.hhPlan
    }
    if (row.hhDisp != null) {
      hasDisp = true
      hhDisp += row.hhDisp
    }
    if (row.hhProg != null) {
      hasProg = true
      hhProg += row.hhProg
    }
    if (row.hhReal != null) {
      hasReal = true
      hhReal += row.hhReal
    }
  }

  return {
    ef: hasEf ? ef : null,
    hhPlan: hasPlan ? hhPlan : null,
    hhDisp: hasDisp ? hhDisp : null,
    hhProg: hasProg ? hhProg : null,
    hhReal: hasReal ? hhReal : null,
    warn: hasReal && hasDisp && hhDisp > 0 && hhReal > hhDisp,
  }
}

/**
 * @deprecated Prefer sumRootWeekTotalsFromApi (pie sobre raíces).
 * Totales de pie / agregados: suma mapas API de stages.
 */
export function sumStageWeekTotalsFromApi(stages, week) {
  return sumRootWeekTotalsFromApi(stages, week)
}

/** Borrador semanal del stage: assigned_people (efectivo) + scheduled_quantity (FTD program.). */
export function weeklyDraftFromRaw(raw) {
  const draft = {}
  const rows = Array.isArray(raw && raw.weekly_distributions) ? raw.weekly_distributions : []
  rows.forEach((row) => {
    const week = Number(row && row.week_number)
    if (!week) {
      return
    }
    draft[week] = {
      assigned_people: toNullableNumber(row.assigned_people),
      scheduled_quantity: toNullableNumber(row.scheduled_quantity),
    }
  })

  const peopleMap = (raw && raw.assigned_people_per_week) || {}
  const qtyMap = (raw && raw.scheduled_quantity_per_week) || {}
  Object.keys(peopleMap).forEach((key) => {
    const week = Number(key)
    if (!week) {
      return
    }
    if (!draft[week]) {
      draft[week] = { assigned_people: null, scheduled_quantity: null }
    }
    if (draft[week].assigned_people == null) {
      draft[week].assigned_people = toNullableNumber(peopleMap[key])
    }
  })
  Object.keys(qtyMap).forEach((key) => {
    const week = Number(key)
    if (!week) {
      return
    }
    if (!draft[week]) {
      draft[week] = { assigned_people: null, scheduled_quantity: null }
    }
    if (draft[week].scheduled_quantity == null) {
      draft[week].scheduled_quantity = toNullableNumber(qtyMap[key])
    }
  })

  return draft
}

function toNullableNumber(value) {
  if (value == null || value === '') {
    return null
  }
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export function isStageNode(node) {
  return node && Number(node.rowKind) === ROW_KIND_STAGE
}

export function canExpandNode(node) {
  return node && (Number(node.childrenCount) || 0) > 0
}

/** year_month YYYY-MM a partir de mesInicio + índice. */
export function yearMonthAt(mesInicio, mi) {
  const [y0, m0] = String(mesInicio || '').split('-').map(Number)
  const d = new Date(y0 || new Date().getFullYear(), ((m0 || 1) - 1) + (Number(mi) || 0), 1)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  return `${d.getFullYear()}-${month}`
}

/** % mensual del nodo (distribuciones takeoff) para el índice de mes. */
export function pctFromNode(node, mesInicio, mi) {
  const ym = yearMonthAt(mesInicio, mi)
  const list = (node && node.monthlyDistributions) || []
  const found = list.find((item) => item && item.year_month === ym)
  return found ? Number(found.percent) || 0 : 0
}

/**
 * Índice HH ajustado del stage (campo persistido adjusted_man_hour_index).
 * @returns {{indiceAj:number|null}}
 */
export function budgetIndices(node) {
  const s = (node && node.takeoffStats) || {}
  const indiceAj = s.adjusted_man_hour_index != null && Number.isFinite(Number(s.adjusted_man_hour_index))
    ? Number(s.adjusted_man_hour_index)
    : null
  return { indiceAj }
}

/**
 * Totales tipo stageTotals a partir de takeoffStats del API.
 * índice: adjusted_man_hour_index persistido del stage (sin cálculo ni fallback).
 * @returns {{cantidad:number,ejecutado:number,saldo:number,indiceHH:number|null,hhTotal:number|null,hhTerminar:number|null}}
 */
export function nodeTotals(node) {
  const s = (node && node.takeoffStats) || {}
  const cantidad = Number(s.total_scope) || 0
  const ejecutado = Number(s.executed_accum) || 0
  const saldo = s.balance != null
    ? Math.max(0, Number(s.balance) || 0)
    : Math.max(0, cantidad - ejecutado)
  const hhTerminarRaw = s.hh_to_finish != null ? Number(s.hh_to_finish) : null
  const hhTerminar = hhTerminarRaw != null && Number.isFinite(hhTerminarRaw)
    ? Math.max(0, hhTerminarRaw)
    : null
  const indiceHH = s.adjusted_man_hour_index != null && Number.isFinite(Number(s.adjusted_man_hour_index))
    ? Number(s.adjusted_man_hour_index)
    : null

  return {
    cantidad,
    ejecutado,
    saldo,
    indiceHH,
    hhTotal: s.scheduled_man_hours != null && Number.isFinite(Number(s.scheduled_man_hours))
      ? Number(s.scheduled_man_hours)
      : null,
    hhTerminar,
  }
}

export function rowKeyForNode(node) {
  return String(node && node.id != null ? node.id : '')
}

/**
 * HH de una etapa en una semana (misma lógica que la plantilla semanal).
 * Si no hay efectivo/programado manual, estima a partir de lo planificado
 * para que el dashboard no quede vacío.
 *
 * @returns {{plan:number|null, disp:number|null, prog:number|null, real:number|null}}
 */
export function dashStageWeekHh(node, week, ctx) {
  const {
    semanaActual,
    mesInicio,
    vals = {},
    hhSemana = 0,
    weeksIn,
    monthIdxOfWeek,
  } = ctx

  const stt = nodeTotals(node)
  const rowKey = rowKeyForNode(node)
  const ind = budgetIndices(node).indiceAj
  const mi = typeof monthIdxOfWeek === 'function' ? monthIdxOfWeek(week) : null
  const nWeeks = mi != null && typeof weeksIn === 'function' ? weeksIn(mi) : 4
  const pct = mi != null ? pctFromNode(node, mesInicio, mi) : 0
  const hhFinish = stt.hhTerminar != null ? stt.hhTerminar : 0

  let plan = null
  if (hhFinish > 0 && nWeeks > 0) {
    plan = Math.round((pct > 0 ? (hhFinish * pct / 100 / nWeeks) : (hhFinish / nWeeks)) * 100) / 100
  }

  const v = vals[`${week}||${rowKey}`] || {}
  let progUnits = v.prog != null ? Number(v.prog) : null
  let ef = v.ef != null ? Number(v.ef) : null

  let prog = null
  if (ind != null && progUnits != null && progUnits > 0) {
    prog = Math.round(ind * progUnits * 100) / 100
  } else if (plan != null && plan > 0) {
    // Sin FTD programada manual: se asume = planificada
    prog = plan
  }

  if ((ef == null || ef <= 0) && hhSemana > 0 && prog != null && prog > 0) {
    ef = Math.max(1, Math.ceil(prog / hhSemana))
  }

  const disp = (ef != null && ef > 0 && hhSemana > 0)
    ? Math.round(ef * hhSemana * 100) / 100
    : null

  let real = null
  if (week > semanaActual) {
    real = null
  } else if (plan != null && plan > 0 && stt.cantidad > 0) {
    const ratio = Math.min(1, Math.max(0, stt.ejecutado / stt.cantidad))
    real = Math.round(plan * (0.55 + 0.45 * ratio) * 100) / 100
  } else if (prog != null && week < semanaActual) {
    real = prog
  }

  return { plan, disp, prog, real }
}

/**
 * Agrega HH de varias etapas en una semana.
 * @returns {{plan:number|null, disp:number|null, prog:number|null, real:number|null}}
 */
export function dashAggregateStagesWeek(stages, week, ctx) {
  let plan = 0
  let disp = 0
  let prog = 0
  let real = 0
  let hasPlan = false
  let hasDisp = false
  let hasProg = false
  let hasReal = false

  for (const stage of stages || []) {
    const hh = dashStageWeekHh(stage, week, ctx)
    if (hh.plan != null) {
      hasPlan = true
      plan += hh.plan
    }
    if (hh.disp != null) {
      hasDisp = true
      disp += hh.disp
    }
    if (hh.prog != null) {
      hasProg = true
      prog += hh.prog
    }
    if (hh.real != null) {
      hasReal = true
      real += hh.real
    }
  }

  return {
    plan: hasPlan ? Math.round(plan * 100) / 100 : null,
    disp: hasDisp ? Math.round(disp * 100) / 100 : null,
    prog: hasProg ? Math.round(prog * 100) / 100 : null,
    real: hasReal ? Math.round(real * 100) / 100 : null,
  }
}
